'use client'

import { useEffect, useRef } from 'react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Music,
  Play,
  ListMusic,
  X,
  Clock,
  CheckCircle,
} from 'lucide-react'
import Image from 'next/image'
import { useTranslations } from 'next-intl'
import type { DemoSong, DemoEventSetlist, DemoSetlist } from '../demo-types'

// Format remaining time from ms
function formatRemainingTime(ms: number): string {
  const totalMinutes = Math.round(ms / 60000)
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  if (hours > 0) return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`
  return `${minutes}m`
}

export interface SetlistPanelProps {
  eventSetlist: DemoEventSetlist | null
  setlists: DemoSetlist[]
  nowPlaying: { song: DemoSong } | null
  selectedSetlistId: string | null
  onSelectSetlist: (id: string) => void
  onClearSetlist: () => void
  onPlaySong: (position: number) => void
}

export function SetlistPanel({
  eventSetlist,
  setlists,
  nowPlaying,
  selectedSetlistId,
  onSelectSetlist,
  onClearSetlist,
  onPlaySong,
}: SetlistPanelProps) {
  const t = useTranslations('demo')
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const currentSongRef = useRef<HTMLDivElement>(null)

  // Auto-scroll to current/next song
  const focusIndex = eventSetlist?.allSongs.findIndex(s => s.isCurrentlyPlaying || s.isNext) ?? -1

  useEffect(() => {
    if (currentSongRef.current && scrollContainerRef.current) {
      const container = scrollContainerRef.current
      const element = currentSongRef.current
      const scrollTo = element.offsetLeft - container.offsetWidth / 2 + element.offsetWidth / 2
      container.scrollTo({ left: Math.max(0, scrollTo), behavior: 'smooth' })
    }
  }, [focusIndex, nowPlaying?.song.id])

  if (setlists.length === 0) return null

  if (!eventSetlist) {
    // No setlist selected - show selector
    return (
      <div className="flex items-center gap-3 p-3 rounded-lg border border-zinc-800 bg-zinc-900/50">
        <ListMusic className="h-5 w-5 text-violet-400 shrink-0" />
        <span className="text-sm text-zinc-400 hidden sm:inline">{t('setlistLabel')}</span>
        <Select value={selectedSetlistId || ''} onValueChange={onSelectSetlist}>
          <SelectTrigger className="flex-1 max-w-xs bg-zinc-800 border-zinc-700 text-white h-8 text-sm">
            <SelectValue placeholder={t('selectASetlist')} />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800">
            {setlists.map((setlist) => (
              <SelectItem key={setlist.id} value={setlist.id} className="text-zinc-300">
                <div className="flex items-center gap-2">
                  <span>{setlist.name}</span>
                  <span className="text-zinc-500">({setlist.songs.length} songs)</span>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    )
  }

  const songsRemaining = eventSetlist.totalSongs - eventSetlist.songsPlayed

  return (
    <div className="rounded-lg border border-violet-500/30 bg-gradient-to-r from-violet-900/20 to-fuchsia-900/20 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between gap-2 p-2 sm:p-3 border-b border-zinc-800/50">
        <div className="flex items-center gap-2 flex-wrap">
          <ListMusic className="h-4 w-4 text-violet-400 shrink-0" />
          <Badge className="bg-violet-500/20 text-violet-400 border-violet-500/30 text-xs">
            {eventSetlist.setlist.name}
          </Badge>
          <span className="text-xs text-zinc-500">
            {eventSetlist.songsPlayed}/{eventSetlist.totalSongs} played
          </span>
        </div>
        <div className="flex items-center gap-2">
          {eventSetlist.remainingDurationMs > 0 && songsRemaining > 0 && (
            <div className="flex items-center gap-1.5 text-sm font-medium text-amber-400 bg-amber-500/10 border border-amber-500/30 px-2.5 py-1 rounded-full">
              <Clock className="h-3.5 w-3.5" />
              <span>~{formatRemainingTime(eventSetlist.remainingDurationMs)}</span>
              <span className="text-amber-500/70 text-xs">({songsRemaining} left)</span>
            </div>
          )}
          <Button variant="ghost" size="sm" onClick={onClearSetlist} className="h-7 w-7 p-0 text-zinc-400 hover:text-red-400">
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Horizontal scrolling song list */}
      <div ref={scrollContainerRef} className="flex items-stretch gap-2 p-3 overflow-x-auto scrollbar-hide">
        {eventSetlist.allSongs.map((ps) => {
          const isFocusSong = ps.isCurrentlyPlaying || ps.isNext

          return (
            <div
              key={ps.songId}
              ref={isFocusSong ? currentSongRef : undefined}
              className={`flex flex-col items-center gap-1 p-2 rounded-lg transition-all shrink-0 w-20 sm:w-24 group cursor-pointer ${
                ps.isCurrentlyPlaying
                  ? 'bg-green-500/20 border-2 border-green-500 shadow-[0_0_15px_rgba(34,197,94,0.3)]'
                  : ps.isNext
                  ? 'bg-violet-500/15 border-2 border-violet-500 shadow-[0_0_15px_rgba(139,92,246,0.3)]'
                  : ps.isPlayed
                  ? 'bg-zinc-800/30 border border-zinc-700/50'
                  : 'bg-zinc-800/40 border border-zinc-700/50 hover:bg-zinc-800/60 hover:border-zinc-600'
              }`}
              onClick={() => !ps.isCurrentlyPlaying && onPlaySong(ps.position)}
            >
              {/* Status label */}
              <div className="w-full text-center h-4 flex items-center justify-center">
                {ps.isCurrentlyPlaying ? (
                  <span className="text-[10px] font-medium text-green-400 uppercase tracking-wide">{t('playingStatus')}</span>
                ) : ps.isPlayed ? (
                  <div className="flex items-center justify-center gap-0.5 text-zinc-500">
                    <CheckCircle className="h-3 w-3" />
                    <span className="text-[10px]">{t('doneStatus')}</span>
                  </div>
                ) : ps.isNext ? (
                  <span className="text-[10px] font-medium text-violet-400 uppercase tracking-wide">{t('upNext')}</span>
                ) : (
                  <span className="text-[10px] text-zinc-400">#{ps.position}</span>
                )}
              </div>

              {/* Artwork */}
              <div className="relative">
                {ps.song.artworkUrl ? (
                  <Image
                    src={ps.song.artworkUrl}
                    alt=""
                    width={56}
                    height={56}
                    className={`h-12 w-12 sm:h-14 sm:w-14 rounded-lg object-cover ${ps.isPlayed ? 'grayscale opacity-60' : ''}`}
                  />
                ) : (
                  <div className={`h-12 w-12 sm:h-14 sm:w-14 rounded-lg bg-zinc-700 flex items-center justify-center ${ps.isPlayed ? 'opacity-60' : ''}`}>
                    <Music className="h-6 w-6 text-zinc-500" />
                  </div>
                )}
                {ps.isPlayed && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-6 h-6 bg-zinc-900/80 rounded-full flex items-center justify-center">
                      <CheckCircle className="h-4 w-4 text-zinc-400" />
                    </div>
                  </div>
                )}
                {!ps.isCurrentlyPlaying && (
                  <div className="absolute inset-0 bg-black/50 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <Play className="h-6 w-6 text-white" />
                  </div>
                )}
                {ps.isCurrentlyPlaying && (
                  <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center shadow-lg">
                    <Play className="h-3 w-3 text-white" />
                  </div>
                )}
              </div>

              {/* Song title */}
              <p className={`text-xs font-medium text-center truncate w-full ${
                ps.isCurrentlyPlaying ? 'text-green-300'
                : ps.isNext ? 'text-violet-300'
                : ps.isPlayed ? 'text-zinc-500'
                : 'text-white'
              }`}>
                {ps.song.title}
              </p>
              <p className={`text-[10px] text-center truncate w-full hidden sm:block ${ps.isPlayed ? 'text-zinc-600' : 'text-zinc-500'}`}>
                {ps.song.artist}
              </p>
            </div>
          )
        })}

        {eventSetlist.isComplete && (
          <div className="flex flex-col items-center justify-center gap-2 p-3 rounded-lg bg-green-500/10 border border-green-500/30 shrink-0 w-20 sm:w-24">
            <CheckCircle className="h-8 w-8 text-green-400" />
            <span className="text-xs text-green-400 text-center">{t('complete')}</span>
          </div>
        )}
      </div>
    </div>
  )
}
